<mat-toolbar class="base-top-app-bar">
  <!-- Left actions (hamburger menu, etc.) -->
  <ng-content select="[slot=left-actions]"></ng-content>

  <div class="app-logo-status">
    <!-- Clickable Logo -->
    @if (logoLink) {
      <a routerLink="/" class="app-logo-link">
        <div class="app-logo-wrapper">
          <img src="assets/images/permitproof-logo.svg" alt="PermitProof" class="app-logo-img" />
        </div>
      </a>
    }
    <!-- Non-clickable Logo -->
    @if (!logoLink) {
      <span class="app-logo-link">
        <div class="app-logo-wrapper">
          <img src="assets/images/permitproof-logo.svg" alt="PermitProof" class="app-logo-img" />
        </div>
      </span>
    }

    @if (environmentBookingEnabled) {
      <app-environment-booking-banner class="environment-booking-overlay"></app-environment-booking-banner>
    }

    <span class="app-title">PermitProof</span>
  </div>

  <!-- Legal Links (Privacy & Terms) -->
  @if (showLegalLinks) {
    <nav class="legal-nav" aria-label="Legal navigation">
      <a routerLink="/privacy" routerLinkActive="active" aria-label="Privacy Policy">Privacy</a>
      <a routerLink="/terms" routerLinkActive="active" aria-label="Terms of Service">Terms</a>
    </nav>
  }

  <!-- Content area - overridden by child components -->
  <ng-content select="[slot=content]"></ng-content>

  <!-- Spacer -->
  <span class="toolbar-spacer"></span>

  <!-- Additional actions - overridden by child components -->
  <ng-content select="[slot=actions]"></ng-content>

  <!--
    Appearance control (Issue #1966). Deliberately OUTSIDE the signed-in block below: the scheme
    is a property of the browser, not of the account, and a reader on the privacy or terms page
    or on the landing page before signing in needs to be able to change it too.
  -->
  <app-theme-toggle></app-theme-toggle>

  <!-- User Profile Menu - shared across all top app bars -->
  @if (user$ | async; as user) {
    <div class="user-profile">
      <!-- Desktop standalone Balance Display -->
      @if (showBalance) {
        @if (balanceView$ | async; as balanceObj) {
          <!-- Polite live region: load transitions are announced, not conveyed by shimmer alone. -->
          <span class="balance-live-region" role="status" aria-live="polite">{{
            getBalanceAnnouncement(balanceObj.value, balanceObj.status)
          }}</span>
          <div
            class="balance-display desktop-balance"
            data-testid="account-balance"
            [attr.data-balance-status]="balanceObj.status"
            [attr.data-balance-usd]="balanceObj.value ?? ''"
            [ngClass]="getBalanceClass(balanceObj.value, balanceObj.status)"
            [matTooltip]="getBalanceTooltip(balanceObj.status)"
            [attr.aria-label]="getBalanceAriaLabel(balanceObj.value, balanceObj.status)"
            [attr.aria-busy]="balanceObj.status === 'loading'"
            (click)="onBalanceChipActivate(balanceObj.status)"
            (keydown.enter)="onBalanceChipActivate(balanceObj.status)"
            (keydown.space)="onBalanceChipActivate(balanceObj.status); $event.preventDefault()"
            tabindex="0"
            role="button"
          >
            @if (balanceObj.status === 'loaded' && balanceObj.value !== null) {
              <span class="balance-amount"> ${{ balanceObj.value | number: '1.2-2' }} </span>
            } @else if (balanceObj.status === 'error') {
              <mat-icon class="balance-error-icon" aria-hidden="true">sync_problem</mat-icon>
              <span class="balance-error-label">Retry</span>
            } @else {
              <span class="balance-placeholder" aria-hidden="true"></span>
            }
          </div>
        }
      }

      <!-- Profile Button and Mobile Balance Overlay -->
      <div class="profile-button-wrapper">
        <button
          data-testid="account-menu-trigger"
          mat-icon-button
          [matMenuTriggerFor]="userMenu"
          class="profile-button"
        >
          <img
            [src]="user.photoURL || 'assets/default-avatar.svg'"
            [alt]="user.displayName || user.email || 'User'"
            referrerpolicy="no-referrer"
            class="profile-picture"
          />
        </button>
        <!-- Mobile Balance Overlay (hidden on desktop) -->
        @if (showBalance) {
          @if (balanceView$ | async; as balanceObj) {
            @if (balanceObj.status === 'loaded' && balanceObj.value !== null) {
              <div class="mobile-balance-badge" [ngClass]="getBalanceClass(balanceObj.value, balanceObj.status)">
                ${{ balanceObj.value | number: '1.2-2' }}
              </div>
            } @else if (balanceObj.status === 'error') {
              <!-- Tappable so mobile users get the same retry affordance as the desktop chip. -->
              <button
                type="button"
                class="mobile-balance-badge balance-error"
                [matTooltip]="getBalanceTooltip(balanceObj.status)"
                [attr.aria-label]="getBalanceAriaLabel(balanceObj.value, balanceObj.status)"
                (click)="onRetryBalance()"
              >
                <mat-icon class="balance-error-icon" aria-hidden="true">sync_problem</mat-icon>
              </button>
            } @else {
              <div class="mobile-balance-badge balance-unknown" aria-hidden="true">
                <span class="mobile-balance-placeholder"></span>
              </div>
            }
          }
        }
      </div>
      <mat-menu #userMenu="matMenu" class="user-menu">
        <div class="user-info">
          <div class="user-name">{{ user.displayName || 'User' }}</div>
          <div class="user-email">
            <span class="logged-in-label">{{ userRoleLabel }}</span>
            <br />
            {{ user.email }}
          </div>
        </div>
        <mat-divider></mat-divider>
        <!-- Additional menu items - provided by child components -->
        <ng-content select="[slot=user-menu]"></ng-content>
        <!-- Billing & Account -->
        @if (showBilling) {
          <button data-testid="account-menu-billing" mat-menu-item (click)="onNavigateToBilling()">
            <mat-icon>account_balance_wallet</mat-icon>
            <span>Billing & Account</span>
          </button>
        }
        <mat-divider></mat-divider>
        <!-- Admin Console link - only for users with root admin role -->
        @if (hasAdminRole$ | async) {
          <button mat-menu-item (click)="onNavigateToAdmin()">
            <mat-icon>admin_panel_settings</mat-icon>
            <span>Admin Console</span>
          </button>
        }
        @if (hasAdminRole$ | async) {
          <mat-divider></mat-divider>
        }
        <!-- Common sign out option -->
        <button mat-menu-item (click)="onSignOut()">
          <mat-icon>logout</mat-icon>
          <span>Sign Out</span>
        </button>
        <!-- Legal links footer -->
        <mat-divider></mat-divider>
        <div class="menu-legal-links">
          <a routerLink="/privacy" aria-label="Privacy Policy">Privacy</a>
          <span class="separator">•</span>
          <a routerLink="/terms" aria-label="Terms of Service">Terms</a>
        </div>
      </mat-menu>
    </div>
  }
</mat-toolbar>
